import React, { useState } from "react";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import styled, { css } from "styled-components";

interface NewsletterSubscriptionData {
  email: string;
  firstName?: string;
  lastName?: string;
}

type NewsletterVariant = "default" | "inline";

type NewsletterButtonVariant =
  | "primary"
  | "secondary"
  | "tertiary"
  | "link"
  | "green"
  | "white";

interface NewsletterProps {
  listId?: string;
  showNameFields?: boolean;
  placeholder?: string;
  buttonText?: string;
  successMessage?: string;
  className?: string;
  label?: string;
  variant?: NewsletterVariant;
  buttonVariant?: NewsletterButtonVariant;
}

const NewsletterSchema = Yup.object().shape({
  email: Yup.string().email("Invalid email").required("Email is required"),
  firstName: Yup.string().min(2, "Too Short!").max(50, "Too Long!"),
  lastName: Yup.string().min(2, "Too Short!").max(50, "Too Long!"),
});

const StyledForm = styled(Form)`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  max-width: 400px;
  width: 100%;
  display: contents;

  @media (min-width: 768px) {
    flex-direction: row;
    align-items: flex-start;
  }
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  flex: 1;
  width: 100%;

  @media (min-width: 768px) {
    gap: 1rem;
  }
`;

const NameFieldsGroup = styled.div`
  display: flex;
  gap: 0.5rem;
  width: 100%;

  @media (max-width: 767px) {
    flex-direction: column;
  }
`;

const StyledInputGroup = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 4px;
  border-radius: 4px;
  width: 100%;
`;

const StyledInput = styled(Field)`
  padding: 8px 12px;
  border: none;
  font-size: 14px;
  border-top-left-radius: 4px;
  border-bottom-left-radius: 4px;
  transition: border-color 0.2s ease;
  outline: none;
  width: 100%;

  &::placeholder {
    color: #6c757d;
  }
`;

const SubmitButton = styled.button`
  font-family: var(--font-alternative);
  background-color: var(--color-gray90);
  color: white;
  border: 1px solid white;
  padding: 12px 24px;
  border-top-right-radius: 4px;
  border-bottom-right-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all ease-in-out 0.2s;
  white-space: nowrap;

  &:hover:not(:disabled) {
    background-color: var(--color-primary50);
    border-color: var(--color-primary50);
  }

  &:disabled {
    cursor: not-allowed;
    transform: none;
  }

  @media (max-width: 767px) {
    width: 100%;
  }
`;

const ErrorMessage = styled.div`
  color: #dc3545;
  font-size: 14px;
  margin-top: 4px;
  font-family: var(--font-alternative);
`;

/* ---- Inline (compact pill) variant ---- */

const InlineForm = styled(Form)`
  display: flex;
  width: 100%;
`;

const InlineGroup = styled.div`
  display: flex;
  align-items: stretch;
  gap: 8px;
  width: 100%;

  @media (max-width: 767px) {
    flex-direction: column;
  }
`;

const InlineInput = styled(Field)`
  flex: 1;
  min-width: 0;
  padding: 9px 18px;
  border: 1px solid transparent;
  border-radius: 4px;
  background-color: #ffffff;
  font-size: 15px;
  color: var(--color-gray90);
  outline: none;
  transition: border-color 0.2s ease;

  &::placeholder {
    color: var(--color-gray40, #8d9da7);
  }

  &:focus {
    border-color: var(--color-gray90);
  }
`;

/* Mirrors the daskapital-ds Button component's variant palette, so the
 * newsletter's inline submit button and the popup's fallback CTA (which uses
 * the actual Button component) stay visually consistent for the same choice. */
const INLINE_BUTTON_VARIANTS: Record<
  NewsletterButtonVariant,
  { bg: string; color: string; hoverBg: string; hoverColor: string }
> = {
  primary: {
    bg: "var(--color-gray90)",
    color: "#ffffff",
    hoverBg: "var(--color-primary50)",
    hoverColor: "#ffffff",
  },
  secondary: {
    bg: "var(--color-primary50)",
    color: "#ffffff",
    hoverBg: "#ffffff",
    hoverColor: "var(--color-primary50)",
  },
  tertiary: {
    bg: "var(--color-gray90)",
    color: "#ffffff",
    hoverBg: "#ffffff",
    hoverColor: "var(--color-primary50)",
  },
  link: {
    bg: "transparent",
    color: "var(--color-primary50)",
    hoverBg: "transparent",
    hoverColor: "var(--color-primary50)",
  },
  green: {
    bg: "var(--color-secondary50)",
    color: "var(--color-gray90)",
    hoverBg: "var(--color-gray90)",
    hoverColor: "var(--color-secondary50)",
  },
  white: {
    bg: "#ffffff",
    color: "var(--color-gray90)",
    hoverBg: "var(--color-primary50)",
    hoverColor: "#ffffff",
  },
};

const InlineButton = styled.button<{ $variant: NewsletterButtonVariant }>`
  font-family: var(--font-alternative);
  flex: 0 0 auto;
  padding: 9px 28px;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;
  transition: background-color 0.2s ease, color 0.2s ease;

  ${({ $variant }) => {
    const styles = INLINE_BUTTON_VARIANTS[$variant] || INLINE_BUTTON_VARIANTS.primary;
    return css`
      background-color: ${styles.bg};
      color: ${styles.color};

      ${$variant === "link" &&
      css`
        padding-left: 0;
        padding-right: 0;
        text-decoration: underline;
      `}

      &:hover:not(:disabled) {
        background-color: ${styles.hoverBg};
        color: ${styles.hoverColor};
      }
    `;
  }}

  &:disabled {
    cursor: not-allowed;
    opacity: 0.7;
  }
`;

const StatusMessage = styled.div<{ type: "success" | "error" }>`
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 14px;
  margin-top: 1rem;
  width: 100%;
  font-family: var(--font-alternative);

  ${(props) =>
    props.type === "success"
      ? `
    background-color: #d4edda;
    color: #155724;
    border: 1px solid #c3e6cb;
  `
      : `
    background-color: #f8d7da;
    color: #721c24;
    border: 1px solid #f5c6cb;
  `}
`;

const KlaviyoNewsletter: React.FC<NewsletterProps> = ({
  listId,
  showNameFields = false,
  placeholder = "Enter your email address",
  label = "Subscribe to our newsletter",
  buttonText = "Subscribe",
  successMessage = "Thank you for subscribing to our newsletter!",
  className,
  variant = "default",
  buttonVariant = "primary",
}) => {
  const [status, setStatus] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (
    values: NewsletterSubscriptionData,
    { resetForm }: { resetForm: () => void },
  ) => {
    setIsSubmitting(true);
    setStatus(null);

    try {
      const response = await fetch("/api/newsletter/subscribe", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: values.email,
          listId: listId || undefined,
          // firstName: values.firstName || undefined,
          // lastName: values.lastName || undefined,
        }),
      });

      const data = await response.json();

      if (data.success) {
        setStatus({ type: "success", message: data.message || successMessage });
        resetForm();
      } else {
        setStatus({
          type: "error",
          message: data.error || "Something went wrong. Please try again.",
        });
      }
    } catch (error) {
      console.error("Newsletter subscription error:", error);
      setStatus({ type: "error", message: "Network error. Please try again." });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={className}>
      <Formik
        initialValues={{
          email: "",
          firstName: "",
          lastName: "",
        }}
        validationSchema={NewsletterSchema}
        onSubmit={handleSubmit}
      >
        {({ errors, touched, values }) => (
          <>
            {variant === "inline" ? (
              <InlineForm>
                <InlineGroup>
                  <InlineInput
                    name="email"
                    type="email"
                    placeholder={placeholder}
                    autoComplete="email"
                    aria-label={label || placeholder}
                    required
                  />
                  <InlineButton
                    type="submit"
                    $variant={buttonVariant}
                    disabled={isSubmitting || !values.email || !!errors.email}
                  >
                    {isSubmitting ? "Subscribing..." : buttonText}
                  </InlineButton>
                </InlineGroup>
              </InlineForm>
            ) : (
              <StyledForm>
                <InputGroup>
                  {showNameFields && (
                    <NameFieldsGroup>
                      <div style={{ flex: 1 }}>
                        <StyledInput
                          name="firstName"
                          type="text"
                          placeholder="First Name"
                          autoComplete="given-name"
                        />
                        {errors.firstName && touched.firstName && (
                          <ErrorMessage>{errors.firstName}</ErrorMessage>
                        )}
                      </div>
                      <div style={{ flex: 1 }}>
                        <StyledInput
                          name="lastName"
                          type="text"
                          placeholder="Last Name"
                          autoComplete="family-name"
                        />
                        {errors.lastName && touched.lastName && (
                          <ErrorMessage>{errors.lastName}</ErrorMessage>
                        )}
                      </div>
                    </NameFieldsGroup>
                  )}
                  <StyledInputGroup>
                    <div
                      className="d-flex flex-column"
                      /*   style={{ width: "85%" }} */
                    >
                      <p
                        style={{
                          fontSize: "16px",
                          marginBottom: "4px",
                          color: "var(--color-gray90)",
                        }}
                      >
                        {label}
                      </p>
                      <div className="d-flex">
                        <StyledInput
                          label={label}
                          name="email"
                          type="email"
                          placeholder={placeholder}
                          autoComplete="email"
                          required
                        />
                        <SubmitButton
                          type="submit"
                          disabled={
                            isSubmitting || !values.email || !!errors.email
                          }
                        >
                          {isSubmitting ? "Subscribing..." : buttonText}
                        </SubmitButton>
                      </div>
                    </div>
                  </StyledInputGroup>
                </InputGroup>
              </StyledForm>
            )}

            {status && (
              <StatusMessage type={status.type}>{status.message}</StatusMessage>
            )}
          </>
        )}
      </Formik>
    </div>
  );
};

export default KlaviyoNewsletter;
